import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { TruncatedCell } from '../components/common/TruncatedCell';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { CalendarDays, Plus, Trash2, Edit2, Loader2, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';
import { formatDate } from '../lib/formatUtils';
import { exportToExcel } from '../lib/exportUtils';
import { usePageSize } from '../context/PageSizeContext';
import { Pagination } from '../components/Pagination';

export const HolidaysPage = () => {
  const confirm = useConfirmation();
  const { pageSize } = usePageSize();
  const [holidays, setHolidays] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [form, setForm] = useState({ name: '', date: '', type: 'national', description: '' });

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        year: yearFilter,
        page: String(page),
        limit: String(pageSize),
      });
      const res = await api.get(`/holidays?${params.toString()}`);
      if (res.data.success) {
        setHolidays(res.data.data);
        setTotal(res.data.total || 0);
      }
    } catch {
      setHolidays([]);
      setTotal(0);
    } finally { setLoading(false); }
  }, [yearFilter, page, pageSize]);

  // Reset to page 1 when year filter changes
  useEffect(() => { setPage(1); }, [yearFilter]);
  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  const openCreate = () => {
    setEditId(null);
    setForm({ name: '', date: '', type: 'national', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (h) => {
    setEditId(h.id);
    setForm({ name: h.name, date: h.date, type: h.type || 'national', description: h.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.date) {
      toast.error('Name and date are required');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/holidays/${editId}`, form);
        toast.success('Holiday updated');
      } else {
        await api.post('/holidays', form);
        toast.success('Holiday created');
      }
      setDialogOpen(false);
      fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save holiday');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({ title: 'Delete Holiday', description: 'Are you sure you want to delete this holiday? This action cannot be undone.' });
    if (!confirmed) return;
    try {
      await api.delete(`/holidays/${id}`);
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (err) {
      toast.error('Failed to delete holiday');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/holidays?year=${yearFilter}&limit=10000`);
      const rows = res.data?.data || [];
      if (!rows.length) { toast.info('No holidays to export'); return; }
      exportToExcel(rows, [
        { key: 'name', header: 'Holiday' },
        { key: 'date', header: 'Date' },
        { key: 'type', header: 'Type' },
        { key: 'description', header: 'Description' },
      ], `Holidays_${yearFilter}`);
      toast.success(`Exported ${rows.length} holidays`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
    finally { setExporting(false); }
  };

  const getTypeBadge = (type) => {
    const cfg = {
      national: 'bg-blue-500/10 text-blue-500',
      company: 'bg-purple-500/10 text-purple-500',
      optional: 'bg-amber-500/10 text-amber-500',
    };
    return <Badge className={cfg[type] || cfg.national}>{type}</Badge>;
  };

  // Upcoming = future-dated holidays *in the loaded page*. With a 100/page
  // default this is accurate even for the most aggressive holiday calendars.
  const upcomingCount = holidays.filter(h => new Date(h.date) >= new Date()).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="holidays-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Holiday Calendar</h1>
          <p className="text-muted-foreground">Manage company holidays and observances</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} data-testid="holidays-export-btn">
            {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
            Excel
          </Button>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-28" data-testid="year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openCreate} data-testid="add-holiday-button">
            <Plus className="h-4 w-4 mr-2" /> Add Holiday
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <CalendarDays className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="total-holidays">{total}</p>
                <p className="text-xs text-muted-foreground">Total Holidays</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <CalendarDays className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="upcoming-holidays">{upcomingCount}</p>
                <p className="text-xs text-muted-foreground">Upcoming</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <CalendarDays className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="national-holidays">{holidays.filter(h => h.type === 'national').length}</p>
                <p className="text-xs text-muted-foreground">National</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : holidays.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <CalendarDays className="h-12 w-12 mb-4 opacity-50" />
              <p>No holidays found for {yearFilter}</p>
            </div>
          ) : (
            <Table data-testid="holidays-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Holiday</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Day</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {holidays.map(h => {
                  const isPast = new Date(h.date) < new Date();
                  return (
                    <TableRow key={h.id} className={isPast ? 'opacity-60' : ''} data-testid={`holiday-row-${h.id}`}>
                      <TableCell className="font-medium">{h.name}</TableCell>
                      <TableCell>{formatDate(h.date)}</TableCell>
                      <TableCell>{new Date(h.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long' })}</TableCell>
                      <TableCell>{getTypeBadge(h.type)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        <TruncatedCell text={h.description} maxWidth={220} testId={`holiday-desc-${h.id}`} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(h)} data-testid={`edit-holiday-${h.id}`}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(h.id)} className="text-destructive hover:text-destructive" data-testid={`delete-holiday-${h.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Pagination
        page={page}
        total={total}
        pageSize={pageSize}
        onPageChange={setPage}
        testIdPrefix="holidays-pagination"
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="holiday-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
            <DialogDescription>
              {editId ? 'Update the holiday details.' : 'Add a new company holiday to the calendar.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Holiday Name</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Diwali"
                data-testid="holiday-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                data-testid="holiday-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="holiday-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="national">National</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                  <SelectItem value="optional">Optional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional description"
                data-testid="holiday-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-holiday-button">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editId ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
